/* eslint-disable max-lines-per-function */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import {
  DEFAULT_QUERY_STALE_TIME,
  EXTENDED_QUERY_STALE_TIME,
} from '@/lib/constants';
import { HypothesesService } from '@/services/HypothesesService';
import type {
  Hypothesis,
  NewHypothesisInput,
  UpdateHypothesisInput,
} from '@/types/hypotheses';
import { createCacheInvalidationManager } from '@/utils/cacheInvalidation';
import { queryKeys } from '@/utils/queryKeys';

export const useHypotheses = (
  productId?: string,
  options?: { enabled?: boolean }
) => {
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: queryKeys.hypotheses.list(userId, productId),
    enabled: !!userId && (options?.enabled ?? true),
    queryFn: async (): Promise<Hypothesis[]> => {
      if (!userId) {
        return [];
      }
      return HypothesesService.list(supabase, productId, userId);
    },
    staleTime: DEFAULT_QUERY_STALE_TIME,
  });
};

export const useHypothesis = (id?: string) => {
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: queryKeys.hypotheses.detail(userId, id),
    enabled: !!userId && !!id,
    queryFn: async (): Promise<Hypothesis | null> => {
      if (!userId || !id) {
        return null;
      }
      return HypothesesService.get(supabase, id, userId);
    },
    staleTime: EXTENDED_QUERY_STALE_TIME,
  });
};

export const useCreateHypothesis = () => {
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: NewHypothesisInput) => {
      if (!userId) {
        throw new Error('Not authenticated');
      }
      return HypothesesService.create(supabase, userId, input);
    },
    onSuccess: async (created) => {
      queryClient.setQueriesData(
        { queryKey: queryKeys.hypotheses.lists() },
        (old: Hypothesis[]) => {
          if (!old || !Array.isArray(old)) {
            return [created];
          }
          return [created, ...old];
        }
      );

      queryClient.setQueriesData(
        { queryKey: queryKeys.hypotheses.list(userId) },
        (old: Hypothesis[]) => {
          if (!old || !Array.isArray(old)) {
            return [created];
          }
          return [created, ...old];
        }
      );

      const cacheManager = createCacheInvalidationManager(queryClient);
      cacheManager.invalidateHypothesisQueries();

      await queryClient.invalidateQueries({
        queryKey: queryKeys.experiments.hypotheses.all,
      });
    },
  });
};

export const useUpdateHypothesis = () => {
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateHypothesisInput) => {
      if (!userId) {
        throw new Error('Not authenticated');
      }
      return HypothesesService.update(supabase, userId, input);
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.hypotheses.all });

      const previousDetail = queryClient.getQueryData(
        queryKeys.hypotheses.detail(userId, variables.id)
      );
      const previousLists = queryClient.getQueriesData({
        queryKey: queryKeys.hypotheses.lists(),
      });

      queryClient.setQueryData(
        queryKeys.hypotheses.detail(userId, variables.id),
        (old: Hypothesis) =>
          old
            ? { ...old, ...variables, updated_at: new Date().toISOString() }
            : old
      );

      queryClient.setQueriesData(
        { queryKey: queryKeys.hypotheses.lists() },
        (old: Hypothesis[]) => {
          if (!old || !Array.isArray(old)) {
            return old;
          }
          return old.map((item: Hypothesis) =>
            item.id === variables.id
              ? { ...item, ...variables, updated_at: new Date().toISOString() }
              : item
          );
        }
      );

      queryClient.setQueriesData(
        { queryKey: queryKeys.hypotheses.searches() },
        (old: Hypothesis[]) => {
          if (!old || !Array.isArray(old)) {
            return old;
          }
          return old.map((item: Hypothesis) =>
            item.id === variables.id
              ? { ...item, ...variables, updated_at: new Date().toISOString() }
              : item
          );
        }
      );

      return { previousDetail, previousLists };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queryKeys.hypotheses.detail(userId, _variables.id),
          context.previousDetail
        );
      }
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData(
        queryKeys.hypotheses.detail(userId, updated.id),
        updated
      );

      queryClient.setQueriesData(
        { queryKey: queryKeys.hypotheses.lists() },
        (old: Hypothesis[]) => {
          if (!old || !Array.isArray(old)) {
            return old;
          }

          return old.map((item: Hypothesis) =>
            item.id === updated.id ? updated : item
          );
        }
      );

      queryClient.setQueriesData(
        { queryKey: queryKeys.hypotheses.searches() },
        (old: Hypothesis[]) => {
          if (!old || !Array.isArray(old)) {
            return old;
          }
          return old.map((item: Hypothesis) =>
            item.id === updated.id ? updated : item
          );
        }
      );
    },
    onSettled: () => {
      const cacheManager = createCacheInvalidationManager(queryClient);
      cacheManager.invalidateHypothesisQueries();
    },
  });
};

export const useDeleteHypothesis = () => {
  const supabase = useSupabaseClient();
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!userId) {
        throw new Error('Not authenticated');
      }
      return HypothesesService.remove(supabase, userId, id);
    },
    onSuccess: async (_ok, id) => {
      queryClient.setQueriesData(
        { queryKey: queryKeys.hypotheses.lists() },
        (old: Hypothesis[]) => {
          if (!old || !Array.isArray(old)) {
            return old;
          }
          return old.filter((item: Hypothesis) => item.id !== id);
        }
      );

      queryClient.removeQueries({
        queryKey: queryKeys.hypotheses.detail(userId, id),
      });

      const cacheManager = createCacheInvalidationManager(queryClient);
      cacheManager.invalidateHypothesisQueries();
    },
  });
};
